import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axiosConn from '../../axiosConn';
import JGLocalOpsMark from '../../components/common/JGLocalOpsMark';
import { AuthContext } from '../../context/AuthContext';
import useLocalOpsAccessPlayer from '../../hooks/useLocalOpsAccessPlayer';
import { canViewLocalOpsOps } from '../../utils/localOpsOpsAccess';
import './localOpsProofsAdmin.css';

const PROOFS_ENDPOINT = 'local-ops/admin/proofs';
const MISSING_PROOFS_ENDPOINT = `${PROOFS_ENDPOINT}/missing`;
const ENSURE_PROOF_ENDPOINT = `${PROOFS_ENDPOINT}/ensure`;
const PROOF_LINE = 'Post a Local Task. Watch it get done.';
const FALLBACK_HELPER_COPY = 'Completed by a local JG LocalOps helper';
const VERIFICATION_COPY = 'Verified completion through JG LocalOps';

const buildDefaultFilters = () => ({
  regionSlug: 'camden',
  taskType: '',
  mediaStatus: '',
  helperRecognition: '',
  search: '',
});

const formatDate = (value) => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString();
};

const formatAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return '';
  return `$${amount.toFixed(2)}`;
};

const safeText = (value, fallback = '') => {
  const clean = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
  return clean || fallback;
};

const formatDuration = (value) => {
  const totalSeconds = Number(value);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '';
  const rounded = Math.round(totalSeconds);
  if (rounded < 60) return `${rounded}s`;
  const minutes = Math.floor(rounded / 60);
  const seconds = rounded % 60;
  if (minutes < 60) return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

const getAcceptedDuration = (proof) =>
  formatDuration(proof?.acceptedInSeconds ?? proof?.velocity?.acceptedInSeconds);

const getCompletedDuration = (proof) =>
  formatDuration(proof?.completedInSeconds ?? proof?.velocity?.completedInSeconds);

const getHelperRewardCopy = (proof) => {
  if (proof?.helperRewardReleased !== true && proof?.helperReward?.released !== true) {
    return '';
  }
  return safeText(
    proof?.helperReward?.copy,
    'Helper reward released after verification',
  );
};

const buildReceiptRows = (proof) => {
  const acceptedDuration = getAcceptedDuration(proof);
  const completedDuration = getCompletedDuration(proof);
  const helperRewardCopy = getHelperRewardCopy(proof);
  const rows = [
    {
      label: 'What happened',
      value: safeText(proof?.taskLabel, 'Local task completed'),
    },
    {
      label: 'Where',
      value: safeText(proof?.regionLabel, 'your area'),
    },
  ];
  if (acceptedDuration) rows.push({ label: 'Accepted', value: `In ${acceptedDuration}` });
  if (completedDuration) rows.push({ label: 'Completed', value: `In ${completedDuration}` });
  if (helperRewardCopy) {
    rows.push({ label: 'Helper reward', value: helperRewardCopy });
  }
  rows.push({ label: 'Verification', value: 'JG LocalOps completion' });
  rows.push({ label: 'Proof ID', value: safeText(proof?.proofId, '--') });
  rows.push({
    label: 'Task ID',
    value: safeText(proof?.taskId || proof?.audit?.taskId, '--'),
  });
  return rows;
};

const normalizeProofsResponse = (response) => {
  const data = response?.data?.data || response?.data || {};
  return Array.isArray(data.proofs) ? data.proofs : [];
};

const normalizeMissingProofsResponse = (response) => {
  const data = response?.data?.data || response?.data || {};
  return Array.isArray(data.tasks) ? data.tasks : [];
};

const normalizeEnsureProofResponse = (response) =>
  response?.data?.data?.proof || response?.data?.proof || null;

const stripApi = (url) =>
  typeof url === 'string' ? url.replace(/\/api\/v1\/?$/, '') : '';

const DEFAULT_PROOF_SHARE_BASE = 'https://api.joegalaxy.net';

const getProofShareOrigin = () =>
  stripApi(process.env.REACT_APP_SHARE_BASE_URL) ||
  stripApi(process.env.REACT_APP_AZURE_API_BASE_URL) ||
  stripApi(process.env.REACT_APP_API_BASE_URL_PROD) ||
  stripApi(process.env.REACT_APP_API_BASE_URL) ||
  DEFAULT_PROOF_SHARE_BASE;

const buildPublicProofUrl = (sharePath) => {
  if (!sharePath || typeof window === 'undefined') return '';
  return new URL(sharePath, getProofShareOrigin()).toString();
};

const CopyButton = ({ sharePath, onCopied }) => {
  const publicUrl = buildPublicProofUrl(sharePath);
  return (
    <button
      type="button"
      className="localops-proofs-admin__secondary"
      disabled={!publicUrl}
      onClick={async (event) => {
        event.stopPropagation();
        if (!publicUrl) return;
        await navigator.clipboard?.writeText(publicUrl);
        onCopied?.(publicUrl);
      }}
    >
      Copy link
    </button>
  );
};

const ProofStatusPill = ({ children, tone = 'neutral' }) => (
  <span className={`localops-proofs-admin__pill is-${tone}`}>{children}</span>
);

const ProofPreview = ({ proof, loading = false, onOpenProof, onCopied }) => {
  if (!proof) {
    return (
      <section className="localops-proofs-admin__preview is-empty">
        <JGLocalOpsMark />
        <h2>Select a proof</h2>
        <p>Preview safe public proof fields before copying or opening.</p>
      </section>
    );
  }

  const mediaStatus = proof.audit?.mediaStatus;
  const helperStatus = proof.audit?.helperRecognitionStatus;
  const safeMedia = Array.isArray(proof.safeMedia) ? proof.safeMedia : [];
  const heroMedia = safeMedia.find((item) => item?.url);
  const taskLabel = safeText(proof.taskLabel, 'Local task completed');
  const regionLabel = safeText(proof.regionLabel, 'your area');
  const helperLine = proof.helper?.handle
    ? `Completed by ${proof.helper.handle}`
    : proof.helperFallback || FALLBACK_HELPER_COPY;
  const receiptRows = buildReceiptRows(proof);

  return (
    <section className="localops-proofs-admin__preview">
      <div className="localops-proofs-admin__preview-head">
        <JGLocalOpsMark />
        <div>
          <p>Admin Action Receipt preview</p>
          <h2>{taskLabel}</h2>
          {loading ? (
            <span className="localops-proofs-admin__detail-status">
              Refreshing proof detail...
            </span>
          ) : null}
        </div>
      </div>

      <div className="localops-proofs-admin__preview-media">
        {heroMedia ? (
          heroMedia.type === 'video' ? (
            <video src={heroMedia.url} controls playsInline preload="metadata" />
          ) : (
            <img src={heroMedia.url} alt="" />
          )
        ) : (
          <div data-testid="proof-admin-fallback">
            <JGLocalOpsMark />
            <strong>Contextual fallback used</strong>
            <span>No share-safe media.</span>
          </div>
        )}
      </div>

      <div className="localops-proofs-admin__preview-copy">
        <p className="localops-proofs-admin__kicker">
          Task completed in {regionLabel}
        </p>
        <h3>{PROOF_LINE}</h3>
        <p>{helperLine}</p>
      </div>

      <div className="localops-proofs-admin__helper-chip">
        {proof.helper?.profilePhoto ? (
          <img src={proof.helper.profilePhoto} alt="" />
        ) : (
          <span>JG</span>
        )}
        <div>
          <strong>{helperLine}</strong>
          <small>{VERIFICATION_COPY}</small>
        </div>
      </div>

      <h3 className="localops-proofs-admin__receipt-title">
        LocalOps Action Receipt
      </h3>
      <div className="localops-proofs-admin__meta-grid">
        {receiptRows.map((row) => (
          <div key={row.label}>
            <span>{row.label}</span>
            <strong>{row.value}</strong>
          </div>
        ))}
        <div>
          <span>Media</span>
          <strong>
            {mediaStatus?.hasSafeMedia
              ? `${mediaStatus.safeMediaCount} safe media`
              : 'Contextual fallback'}
          </strong>
        </div>
        <div>
          <span>Helper</span>
          <strong>{helperStatus?.safe ? 'Safe recognition' : 'Fallback copy'}</strong>
        </div>
        <div>
          <span>Completed</span>
          <strong>{formatDate(proof.completedAt)}</strong>
        </div>
        <div>
          <span>Created</span>
          <strong>{formatDate(proof.audit?.createdAt)}</strong>
        </div>
      </div>

      <div className="localops-proofs-admin__preview-actions">
        <button
          type="button"
          className="localops-proofs-admin__secondary"
          disabled={!buildPublicProofUrl(proof.sharePath)}
          onClick={(event) => {
            event.stopPropagation();
            onOpenProof?.(proof);
          }}
        >
          Open proof
        </button>
        <CopyButton sharePath={proof.sharePath} onCopied={onCopied} />
      </div>
    </section>
  );
};

const MissingProofsList = ({
  tasks,
  loading,
  generatingTaskId,
  onGenerate,
  onOpenProof,
  onCopied,
}) => (
  <section className="localops-proofs-admin__list">
    <div className="localops-proofs-admin__list-head">
      <div>
        <h2>Missing Proofs</h2>
        <p>Completed tasks that do not yet have a proof link.</p>
      </div>
      <span>{loading ? 'Loading...' : `${tasks.length} tasks`}</span>
    </div>
    {tasks.length === 0 && !loading ? (
      <p className="localops-proofs-admin__empty">
        No completed tasks are missing proof links for these filters.
      </p>
    ) : null}
    {tasks.map((task) => {
      const proof = task.generatedProof || task.proof || null;
      const generated = Boolean(proof?.sharePath);
      const amount = formatAmount(task.amountUSD);
      const mediaStatus = task.mediaStatus || {};
      const helperStatus = task.helperRecognitionStatus || {};
      return (
        <article
          key={task.taskId}
          className={`localops-proofs-admin__row${generated ? ' is-generated' : ''}`}
        >
          <div className="localops-proofs-admin__row-main is-static">
            <strong>{task.taskLabel || 'Local task completed'}</strong>
            <span>
              {[task.regionLabel, task.taskType, task.starterId]
                .filter(Boolean)
                .join(' · ') || 'Completed LocalOps task'}
            </span>
            <small>
              {formatDate(task.completedAt)}
              {amount ? ` · ${amount}` : ''}
            </small>
          </div>
          <div className="localops-proofs-admin__row-pills">
            <ProofStatusPill tone={generated ? 'green' : 'yellow'}>
              {generated ? 'Proof generated' : 'Missing proof'}
            </ProofStatusPill>
            <ProofStatusPill tone={mediaStatus.hasSafeMedia ? 'green' : 'yellow'}>
              {mediaStatus.hasSafeMedia ? 'Safe media available' : 'Contextual fallback'}
            </ProofStatusPill>
            <ProofStatusPill tone={helperStatus.safe ? 'green' : 'neutral'}>
              {helperStatus.safe ? 'Helper recognition available' : 'Helper fallback'}
            </ProofStatusPill>
          </div>
          <div className="localops-proofs-admin__row-actions">
            {generated ? (
              <>
                <button
                  type="button"
                  className="localops-proofs-admin__secondary"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenProof(proof);
                  }}
                >
                  Open proof
                </button>
                <CopyButton sharePath={proof.sharePath} onCopied={onCopied} />
              </>
            ) : (
              <button
                type="button"
                className="localops-proofs-admin__primary"
                disabled={generatingTaskId === task.taskId}
                onClick={() => onGenerate(task)}
              >
                {generatingTaskId === task.taskId ? 'Generating...' : 'Generate proof'}
              </button>
            )}
          </div>
        </article>
      );
    })}
  </section>
);

export default function LocalOpsProofsAdmin() {
  const { player } = useContext(AuthContext);
  const { accessPlayer } = useLocalOpsAccessPlayer(player);
  const allowed = canViewLocalOpsOps(accessPlayer);
  const [activeTab, setActiveTab] = useState('proofs');
  const [filters, setFilters] = useState(() => buildDefaultFilters());
  const [proofs, setProofs] = useState([]);
  const [missingTasks, setMissingTasks] = useState([]);
  const [selectedProof, setSelectedProof] = useState(null);
  const [loading, setLoading] = useState(false);
  const [missingLoading, setMissingLoading] = useState(false);
  const [detailLoadingProofId, setDetailLoadingProofId] = useState('');
  const [generatingTaskId, setGeneratingTaskId] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const authConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${player?.token || ''}`,
      },
    }),
    [player?.token],
  );

  const fetchProofs = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    setError('');
    try {
      const params = Object.entries(filters).reduce((acc, [key, value]) => {
        const clean = String(value || '').trim();
        if (clean) acc[key] = clean;
        return acc;
      }, {});
      const response = await axiosConn.get(PROOFS_ENDPOINT, {
        ...authConfig,
        params,
      });
      const rows = normalizeProofsResponse(response);
      setProofs(rows);
      setDetailLoadingProofId((current) =>
        rows.some((proof) => proof.proofId === current) ? current : '',
      );
      setSelectedProof((current) => {
        if (!current) return rows[0] || null;
        return rows.find((proof) => proof.proofId === current.proofId) || rows[0] || null;
      });
    } catch (err) {
      setError('Unable to load proof inbox.');
      setProofs([]);
      setSelectedProof(null);
      setDetailLoadingProofId('');
    } finally {
      setLoading(false);
    }
  }, [allowed, authConfig, filters]);

  const fetchMissingProofs = useCallback(async () => {
    if (!allowed) return;
    setMissingLoading(true);
    setError('');
    try {
      const params = Object.entries(filters).reduce((acc, [key, value]) => {
        const clean = String(value || '').trim();
        if (clean) acc[key] = clean;
        return acc;
      }, {});
      const response = await axiosConn.get(MISSING_PROOFS_ENDPOINT, {
        ...authConfig,
        params,
      });
      setMissingTasks(normalizeMissingProofsResponse(response));
    } catch (err) {
      setError('Unable to load missing proof tasks.');
      setMissingTasks([]);
    } finally {
      setMissingLoading(false);
    }
  }, [allowed, authConfig, filters]);

  useEffect(() => {
    if (activeTab === 'missing') {
      fetchMissingProofs();
      return;
    }
    fetchProofs();
  }, [activeTab, fetchMissingProofs, fetchProofs]);

  const loadDetail = useCallback(
    async (proof) => {
      if (!proof?.proofId) return;
      setSelectedProof(proof);
      setDetailLoadingProofId(proof.proofId);
      setError('');
      try {
        const response = await axiosConn.get(
          `${PROOFS_ENDPOINT}/${encodeURIComponent(proof.proofId)}`,
          authConfig,
        );
        const detail = response?.data?.data || proof;
        setSelectedProof((current) =>
          current?.proofId === proof.proofId ? { ...proof, ...detail } : current,
        );
      } catch (err) {
        setError('Unable to load proof detail.');
        setSelectedProof((current) =>
          current?.proofId === proof.proofId ? proof : current,
        );
      } finally {
        setDetailLoadingProofId((current) =>
          current === proof.proofId ? '' : current,
        );
      }
    },
    [authConfig],
  );

  const openProof = useCallback(
    (proof) => {
      const publicUrl = buildPublicProofUrl(proof?.sharePath);
      if (publicUrl) {
        window.open(publicUrl, '_blank', 'noopener,noreferrer');
      }
    },
    [],
  );

  const generateProofForTask = useCallback(
    async (task) => {
      if (!task?.taskId) return;
      setGeneratingTaskId(task.taskId);
      setError('');
      setNotice('');
      try {
        const response = await axiosConn.post(
          ENSURE_PROOF_ENDPOINT,
          { taskId: task.taskId },
          authConfig,
        );
        const proof = normalizeEnsureProofResponse(response);
        if (!proof?.sharePath) {
          throw new Error('Missing proof sharePath');
        }
        setMissingTasks((current) =>
          current.map((row) =>
            row.taskId === task.taskId
              ? {
                  ...row,
                  proofStatus: 'generated',
                  generatedProof: proof,
                }
              : row,
          ),
        );
        setSelectedProof(proof);
        setDetailLoadingProofId('');
        setNotice('Proof generated.');
      } catch (err) {
        setError('Unable to generate proof for this completed task.');
      } finally {
        setGeneratingTaskId('');
      }
    },
    [authConfig],
  );

  const refreshCurrentTab = useCallback(() => {
    if (activeTab === 'missing') {
      fetchMissingProofs();
      return;
    }
    fetchProofs();
  }, [activeTab, fetchMissingProofs, fetchProofs]);

  if (!allowed) {
    return (
      <main className="localops-proofs-admin">
        <section className="localops-proofs-admin__blocked">
          <JGLocalOpsMark />
          <h1>Proofs unavailable</h1>
          <p>True admin access is required for LocalOps proof operations.</p>
        </section>
      </main>
    );
  }

  return (
    <main className="localops-proofs-admin">
      <header className="localops-proofs-admin__header">
        <div>
          <JGLocalOpsMark />
          <p>LocalOps proof inbox</p>
          <h1>Completion Proofs</h1>
        </div>
        <button
          type="button"
          onClick={refreshCurrentTab}
          disabled={loading || missingLoading}
        >
          {loading || missingLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </header>

      <section className="localops-proofs-admin__tabs" aria-label="Proof inbox sections">
        <button
          type="button"
          className={activeTab === 'proofs' ? 'is-active' : ''}
          onClick={() => setActiveTab('proofs')}
        >
          Proofs
        </button>
        <button
          type="button"
          className={activeTab === 'missing' ? 'is-active' : ''}
          onClick={() => setActiveTab('missing')}
        >
          Missing Proofs
        </button>
      </section>

      <section className="localops-proofs-admin__filters">
        <label>
          Region
          <input
            value={filters.regionSlug}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                regionSlug: event.target.value,
              }))
            }
          />
        </label>
        <label>
          Task type
          <input
            value={filters.taskType}
            placeholder="errand, delivery..."
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                taskType: event.target.value,
              }))
            }
          />
        </label>
        <label>
          Media
          <select
            value={filters.mediaStatus}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                mediaStatus: event.target.value,
              }))
            }
          >
            <option value="">Any</option>
            <option value="safe_media">Safe media</option>
            <option value="contextual_fallback">Fallback</option>
          </select>
        </label>
        <label>
          Helper
          <select
            value={filters.helperRecognition}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                helperRecognition: event.target.value,
              }))
            }
          >
            <option value="">Any</option>
            <option value="safe">Safe recognition</option>
            <option value="fallback">Fallback copy</option>
          </select>
        </label>
        <label>
          Search
          <input
            value={filters.search}
            placeholder="proofId, taskId, title"
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                search: event.target.value,
              }))
            }
          />
        </label>
      </section>

      {notice ? <div className="localops-proofs-admin__notice">{notice}</div> : null}
      {error ? <div className="localops-proofs-admin__error">{error}</div> : null}

      <div className="localops-proofs-admin__layout">
        {activeTab === 'missing' ? (
          <MissingProofsList
            tasks={missingTasks}
            loading={missingLoading}
            generatingTaskId={generatingTaskId}
            onGenerate={generateProofForTask}
            onOpenProof={openProof}
            onCopied={() => setNotice('Public proof link copied.')}
          />
        ) : (
          <section className="localops-proofs-admin__list">
            <div className="localops-proofs-admin__list-head">
              <h2>Proof snapshots</h2>
              <span>{loading ? 'Loading...' : `${proofs.length} proofs`}</span>
            </div>
            {proofs.length === 0 && !loading ? (
              <p className="localops-proofs-admin__empty">
                No proof snapshots match these filters.
              </p>
            ) : null}
            {proofs.map((proof) => {
              const selected = selectedProof?.proofId === proof.proofId;
              const mediaStatus = proof.audit?.mediaStatus;
              const helperStatus = proof.audit?.helperRecognitionStatus;
              return (
                <article
                  key={proof.proofId}
                  className={`localops-proofs-admin__row${selected ? ' is-selected' : ''}`}
                >
                  <button
                    type="button"
                    className="localops-proofs-admin__row-main"
                    onClick={() => loadDetail(proof)}
                  >
                    <strong>{proof.taskLabel || 'Local task completed'}</strong>
                    <span>{proof.regionLabel || 'Unknown region'}</span>
                    <small>{formatDate(proof.completedAt)}</small>
                  </button>
                  <div className="localops-proofs-admin__row-pills">
                    <ProofStatusPill tone={mediaStatus?.hasSafeMedia ? 'green' : 'yellow'}>
                      {mediaStatus?.hasSafeMedia ? 'safe media' : 'fallback'}
                    </ProofStatusPill>
                    <ProofStatusPill tone={helperStatus?.safe ? 'green' : 'neutral'}>
                      {helperStatus?.safe ? 'helper safe' : 'helper fallback'}
                    </ProofStatusPill>
                  </div>
                  <div className="localops-proofs-admin__row-actions">
                    <button
                      type="button"
                      className="localops-proofs-admin__secondary"
                      onClick={(event) => {
                        event.stopPropagation();
                        openProof(proof);
                      }}
                    >
                      Open proof
                    </button>
                    <CopyButton
                      sharePath={proof.sharePath}
                      onCopied={() => setNotice('Public proof link copied.')}
                    />
                  </div>
                </article>
              );
            })}
          </section>
        )}

        <div>
          <ProofPreview
            proof={selectedProof}
            loading={Boolean(
              detailLoadingProofId && selectedProof?.proofId === detailLoadingProofId,
            )}
            onOpenProof={openProof}
            onCopied={() => setNotice('Public proof link copied.')}
          />
        </div>
      </div>
    </main>
  );
}
